const SPACE_ID = process.env.REACT_APP_CONTENTFUL_SPACE_ABOUT_US;
const ACCESS_TOKEN = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN_ABOUT_US;
const ENVIRONMENT = process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master';

/* Types */
interface ContentfulAsset {
  sys: { id: string };
  fields: {
    file: {
      url: string;
    };
  };
}

interface ContentfulEntry {
  sys: { id: string };
  fields: Record<string, any>;
}

interface RichTextNode {
  nodeType: string;
  value?: string;
  content?: RichTextNode[];
}

interface RichText {
  content: RichTextNode[];
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface ServiceResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

/* Helpers */
const richTextToPlainText = (richText: RichText | null | undefined): string => {
  if (!richText?.content) return '';
  let text = '';
  
  const walk = (node: RichTextNode): void => {
    if (node.nodeType === 'text') {
      text += (node.value || '') + ' ';
    }
    node.content?.forEach(walk);
  };
  
  richText.content.forEach(walk);
  return text.trim();
};

const getAssetUrl = (
  assets: ContentfulAsset[],
  id: string | undefined
): string | null => {
  if (!id) return null;
  const asset = assets.find((x) => x.sys.id === id);
  return asset?.fields?.file?.url ? `https:${asset.fields.file.url}` : null;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const calculateReadTime = (text: string): string => {
  const wordCount = text.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / 200);
  return `${minutes} min read`;
};

/* API CALLS */
export const fetchBlogPosts = async (): Promise<ServiceResponse<BlogPost[]>> => {
  try {
    if (!SPACE_ID || !ACCESS_TOKEN) {
      throw new Error('Contentful credentials not configured');
    }

    const response = await fetch(
      `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT}/entries?access_token=${ACCESS_TOKEN}&content_type=blogPost&order=-sys.createdAt&include=3`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch blog posts: ${response.status}`);
    }

    const data = await response.json();
    const assets: ContentfulAsset[] = data.includes?.Asset || [];
    const entries: ContentfulEntry[] = data.includes?.Entry || [];

    const articles: BlogPost[] = data.items.map((post: any) => {
      const templateId = post.fields.templates?.[0]?.sys?.id;
      const template = entries.find((e) => e.sys.id === templateId);

      const plainText = template?.fields?.description
        ? richTextToPlainText(template.fields.description)
        : '';

      const imageId =
        post.fields.coverImage?.sys?.id ||
        template?.fields.blogCoverImage?.[0]?.sys?.id;

      return {
        id: post.sys.id,
        title: post.fields.title || template?.fields?.blogTitle || 'Untitled',
        excerpt: plainText.slice(0, 200) + (plainText.length > 200 ? '...' : ''),
        image: getAssetUrl(assets, imageId) || '/placeholder-image.jpg',
        author: post.fields.author || 'Team 4um',
        date: formatDate(post.sys.createdAt),
        readTime: calculateReadTime(plainText),
        category: post.fields.categoryCard || 'Article'
      };
    });

    return { success: true, data: articles };
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      data: []
    };
  }
};

/**
 * Fetch a single blog post by ID
 */
export const fetchBlogPostById = async (
  id: string
): Promise<ServiceResponse<any>> => {
  try {
    if (!SPACE_ID || !ACCESS_TOKEN) {
      throw new Error('Contentful credentials not configured');
    }

    // Fetch the blog post
    const postResponse = await fetch(
      `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT}/entries/${id}?access_token=${ACCESS_TOKEN}`
    );

    if (!postResponse.ok) {
      throw new Error(`Failed to fetch blog post: ${postResponse.status}`);
    }

    const post = await postResponse.json();

    // Get template ID
    const templateId = post.fields.templates?.[0]?.sys?.id;
    if (!templateId) {
      throw new Error('No blog content template linked');
    }

    // Fetch the template
    const templateResponse = await fetch(
      `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT}/entries/${templateId}?access_token=${ACCESS_TOKEN}`
    );

    if (!templateResponse.ok) {
      throw new Error(`Failed to fetch template: ${templateResponse.status}`);
    }

    const template = await templateResponse.json();

    // Fetch cover image if exists
    let coverImage = null;
    const coverImageId = post.fields.coverImage?.sys?.id;
    if (coverImageId) {
      const assetResponse = await fetch(
        `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT}/assets/${coverImageId}?access_token=${ACCESS_TOKEN}`
      );
      if (assetResponse.ok) {
        coverImage = await assetResponse.json();
      }
    }

    return {
      success: true,
      data: {
        ...post,
        resolvedTemplate: template,
        resolvedCoverImage: coverImage
      }
    };
  } catch (error) {
    console.error('Error fetching blog post by ID:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      data: null
    };
  }
};

/**
 * Fetch all FAQs from Contentful
 */
export const fetchFaqs = async (): Promise<ServiceResponse<FAQ[]>> => {
  try {
    if (!SPACE_ID || !ACCESS_TOKEN) {
      throw new Error('Contentful credentials not configured');
    }

    const response = await fetch(
      `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT}/entries?access_token=${ACCESS_TOKEN}&content_type=faq&order=sys.createdAt`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch FAQs: ${response.status}`);
    }

    const data = await response.json();

    const faqs: FAQ[] = data.items.map((item: any) => ({
      id: item.sys.id,
      question: item.fields.questions || '',
      answer: item.fields.answers || ''
    }));

    return { success: true, data: faqs };
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      data: []
    };
  }
};